import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const LINES = [
  { type: 'cmd', text: 'whoami' },
  { type: 'out', text: 'Software Engineer · DronaHQ · Mumbai' },
  { type: 'cmd', text: 'cat now.txt' },
  { type: 'out', text: 'Building AI-native low-code tools' },
  { type: 'cmd', text: 'echo $stack' },
  { type: 'out', text: 'React · Node.js · TypeScript · LangGraph · Electron' },
  { type: 'cmd', text: 'cat fun-fact.txt' },
  { type: 'out', text: '10x output. zero meetings wasted.' },
];

const Terminal = () => {
  const [done, setDone] = useState([]);
  const [cur, setCur] = useState({ lineIdx: 0, charIdx: 0 });
  const [active, setActive] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setActive(true); obs.disconnect(); }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    const { lineIdx, charIdx } = cur;
    if (lineIdx >= LINES.length) return;
    const line = LINES[lineIdx];

    if (charIdx < line.text.length) {
      const delay = line.type === 'cmd' ? 55 : 18;
      const t = setTimeout(() => setCur(c => ({ ...c, charIdx: c.charIdx + 1 })), delay);
      return () => clearTimeout(t);
    } else {
      const pause = line.type === 'cmd' ? 350 : 180;
      const t = setTimeout(() => {
        setDone(d => [...d, line]);
        setCur({ lineIdx: lineIdx + 1, charIdx: 0 });
      }, pause);
      return () => clearTimeout(t);
    }
  }, [active, cur]);

  const currentLine = LINES[cur.lineIdx];

  return (
    <div ref={ref} className="terminal-block">
      <div className="terminal-header">
        <span className="terminal-dot dot-red" />
        <span className="terminal-dot dot-yellow" />
        <span className="terminal-dot dot-green" />
        <span className="terminal-label">harsh@portfolio ~ zsh</span>
      </div>
      <div className="terminal-body">
        {done.map((line, i) => (
          <div key={i} className={`t-line t-line--${line.type}`}>
            {line.type === 'cmd' && <span className="t-prompt">~/harsh $ </span>}
            <span>{line.text}</span>
          </div>
        ))}
        {currentLine && cur.lineIdx < LINES.length && (
          <div className={`t-line t-line--${currentLine.type}`}>
            {currentLine.type === 'cmd' && <span className="t-prompt">~/harsh $ </span>}
            <span>
              {currentLine.text.slice(0, cur.charIdx)}
              <span className="t-cursor">▋</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const About = () => (
  <section id="about" className="section about-section">
    <div className="section-inner">
      <motion.div
        className="about-grid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
      >
        <motion.div className="about-image-wrapper" variants={fadeUp}>
          <img src="profile-about.png" alt="Harsh Singh" className="about-avatar" />
        </motion.div>

        <motion.div className="about-text" variants={fadeUp}>
          <p className="section-label">About</p>
          <h2 className="section-title">Who I Am</h2>

          <p>
            Software Engineer with <span className="highlight">3+ years</span> of experience building scalable,
            AI-powered web applications. Recognized as a <span className="highlight">10× engineer</span> for
            delivering high-impact solutions and rapid results.
          </p>
          <p>
            Skilled in multiple programming languages and various databases.
            Passionate about creating unique products that live at the intersection of
            great engineering and great design.
          </p>

          <a
            href="https://www.linkedin.com/posts/harshxdev_dronahq-starperformer2024-gratitude-activity-7301443583188832256-8WuU"
            target="_blank"
            rel="noopener noreferrer"
            className="award-recognition"
          >
            <span className="award-dot" />
            <span>Star Performer of the Year · DronaHQ</span>
          </a>

          <Terminal />

          <div className="about-links">
            <a href="https://www.linkedin.com/in/harshxdev/" target="_blank" rel="noopener noreferrer" className="about-link-btn">
              <svg viewBox="0 0 24 24"><path d="M23.994 24v-.001H24v-8.802c0-4.306-.927-7.623-5.961-7.623-2.42 0-4.044 1.328-4.707 2.587h-.07V7.976H8.489v16.023h4.97v-7.934c0-2.089.396-4.109 2.983-4.109 2.549 0 2.587 2.384 2.587 4.243V24zM.396 7.977h4.976V24H.396zM2.882 0C1.291 0 0 1.291 0 2.882s1.291 2.909 2.882 2.909 2.882-1.318 2.882-2.909A2.884 2.884 0 002.882 0z" /></svg>
              LinkedIn
            </a>
            <a href="https://github.com/HARSHSINGH0" target="_blank" rel="noopener noreferrer" className="about-link-btn">
              <svg viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" /></svg>
              GitHub
            </a>
            <a href="Harsh-Resume.pdf" download className="about-link-btn">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Resume
            </a>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

export default About;
